import { useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";
import type { ProfileMetadata } from "@/lib/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { canManageRevelatio, REVELATIO_TEAM_ID } from "@/lib/auth";

interface ExportRow {
  id: string;
  name: string;
  secret: string;
  created_at: string | null;
  created_by: string;
  team_id: string;
  teams: { name: string } | null;
  totp_categories: { name: string } | null;
}

interface ProfileRow {
  id: string;
  email: string;
  metadata: ProfileMetadata | null;
}

function createdByLabel(profile: ProfileRow | undefined): string {
  if (!profile) return "";
  const name = profile.metadata?.name?.trim();
  if (name) return name;
  return profile.email ?? "";
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return (err as { message: string }).message;
  }
  return "Não foi possível gerar o arquivo XLSX.";
}

export function ExportTOTPButton() {
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleExport = async () => {
    setExporting(true);
    try {
      // created_by FK aponta para auth.users, não profiles — join PostgREST não funciona.
      const { data, error } = await supabase
        .from("totp_codes")
        .select(
          `
          id,
          name,
          secret,
          created_at,
          created_by,
          team_id,
          teams ( name ),
          totp_categories ( name )
        `
        )
        .order("created_at", { ascending: true });

      if (error) throw error;

      const includeRevelatio = user?.id
        ? await canManageRevelatio(user.id)
        : false;
      const codes = ((data ?? []) as unknown as ExportRow[]).filter(
        (row) => row.team_id !== REVELATIO_TEAM_ID || includeRevelatio
      );
      const creatorIds = [...new Set(codes.map((c) => c.created_by).filter(Boolean))];

      const profilesById = new Map<string, ProfileRow>();
      if (creatorIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("id, email, metadata")
          .in("id", creatorIds);

        if (profilesError) throw profilesError;

        for (const profile of (profiles ?? []) as ProfileRow[]) {
          profilesById.set(profile.id, profile);
        }
      }

      const rows = codes.map((row) => ({
        id: row.id,
        name: row.name,
        secret: row.secret,
        team: row.teams?.name ?? "",
        category: row.totp_categories?.name ?? "",
        created_by: createdByLabel(profilesById.get(row.created_by)),
        created_at: row.created_at ?? "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "totp_codes");
      XLSX.writeFile(workbook, `totp_codes_${Date.now()}.xlsx`);
    } catch (err: unknown) {
      console.error("Error exporting totp_codes:", err);
      toast({
        title: "Erro ao exportar",
        description: errorMessage(err),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="rounded-full bg-secondary hover:bg-accent"
      onClick={handleExport}
      disabled={exporting}
      title="Exportar códigos TOTP (XLSX)"
      aria-label="Exportar códigos TOTP (XLSX)"
    >
      {exporting ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <FileSpreadsheet className="h-5 w-5" />
      )}
    </Button>
  );
}
